import { createEmbedBridge } from "./embed_bridge"

export const createViewerEmbedController = ({
  state,
  viewerConfig,
  element,
  selectedObjectLabel,
  selectedNodeKey,
  navigationPointLabel,
  focusSearchEntry,
  setPanelMode,
  selectPlacement,
  focusOnPlacement,
  selectNodeObject,
  focusOnNodeObject,
  selectNavigationPoint,
  focusOnNavigationPoint,
  closePopup,
  renderPopup,
  queueViewMode,
  fitCurrentView,
  resetView
}) => {
  const normalizeRequestedViewMode = (mode) => {
    const normalized = String(mode || "").trim()
    return normalized === "2d-ortho" ? "2d" : normalized
  }

  const currentSelectionPayload = () => {
    if (state.selectedPlacement) {
      return {
        kind: "placement",
        id: state.selectedPlacement.id,
        thingId: state.selectedPlacement.thing?.id || null,
        title: state.selectedPlacement.thing?.name || "Unnamed object",
        subtitle: state.selectedPlacement.thing?.kind || "",
        position: state.selectedPlacement.render_position || state.selectedPlacement.position || null
      }
    }

    if (state.selectedNavigationPoint) {
      return {
        kind: "navigation_point",
        id: state.selectedNavigationPoint.id,
        title: navigationPointLabel(state.selectedNavigationPoint),
        position: state.selectedNavigationPoint.position || null
      }
    }

    if (state.selectedObject) {
      const nodeKey = selectedNodeKey()
      const mapping = state.mappingsByNodeKey.get(nodeKey)

      return {
        kind: "mapping",
        modelNodeKey: nodeKey,
        modelNodeName: state.selectedObject.name || "",
        thingId: mapping?.thing?.id || null,
        title: mapping?.thing?.name || selectedObjectLabel(),
        subtitle: mapping?.thing?.kind || state.selectedObject.type || "",
        externalMappings: Array.from(state.nodeMappingsByNodeKey.get(nodeKey) || []).map((entry) => ({
          id: entry.id,
          integrationId: entry.integration_id,
          integrationName: entry.integration_name,
          externalId: entry.external_id,
          externalLabel: entry.external_label
        }))
      }
    }

    return null
  }

  const embedSnapshot = () => ({
    mode: state.mode,
    capabilities: state.capabilities,
    plan: {
      id: viewerConfig?.plan?.id || null,
      name: element.dataset.planName || viewerConfig?.plan?.name || ""
    },
    viewMode: state.viewMode,
    selection: currentSelectionPayload()
  })

  const focusEmbedObject = (criteria = {}) => {
    if (!criteria || typeof criteria !== "object") {
      throw new Error("Focus criteria must be an object.")
    }

    const focusPlacementEntry = (placement) => {
      if (!placement) return false

      if (state.mappingMode) setPanelMode("place")
      selectPlacement(placement)
      focusOnPlacement(placement)
      if (state.mappingMode) {
        closePopup()
      } else {
        state.popupEntry = { kind: "placement", ...placement }
        renderPopup()
      }
      return true
    }

    const focusMappingEntry = (mapping) => {
      if (!mapping?.model_node_key) return false

      const object = state.nodesByKey.get(mapping.model_node_key)
      if (!object) return false

      if (state.mappingMode) setPanelMode("tag")
      selectNodeObject(object)
      focusOnNodeObject(object)
      if (state.mappingMode) {
        closePopup()
      } else {
        state.popupEntry = { kind: "mapping", ...mapping }
        renderPopup()
      }
      return true
    }

    const focusNavigationPointEntry = (navigationPoint) => {
      if (!navigationPoint) return false

      if (state.mappingMode) setPanelMode("navigate")
      selectNavigationPoint(navigationPoint)
      focusOnNavigationPoint(navigationPoint)
      closePopup()
      return true
    }

    const placementId = criteria.placementId || (criteria.kind === "placement" ? criteria.id : null)
    if (placementId && focusPlacementEntry(state.placementsById.get(String(placementId)) || state.placementsById.get(placementId))) {
      return embedSnapshot()
    }

    const navigationPointId = criteria.navigationPointId || (criteria.kind === "navigation_point" ? criteria.id : null)
    if (navigationPointId && focusNavigationPointEntry(state.navigationPointsById.get(String(navigationPointId)) || state.navigationPointsById.get(navigationPointId))) {
      return embedSnapshot()
    }

    if (criteria.modelNodeKey && focusMappingEntry(state.mappingsByNodeKey.get(criteria.modelNodeKey))) {
      return embedSnapshot()
    }

    if (criteria.externalId) {
      const externalMapping = Array.from(state.nodeMappingsById.values()).find((entry) => entry.external_id === criteria.externalId)
      if (externalMapping?.thing_placement_id && focusPlacementEntry(state.placementsById.get(externalMapping.thing_placement_id))) {
        return embedSnapshot()
      }
      if (focusMappingEntry(state.mappingsByNodeKey.get(externalMapping?.model_node_key))) {
        return embedSnapshot()
      }
    }

    if (criteria.thingId) {
      const placement = Array.from(state.placementsById.values()).find((entry) => entry.thing?.id === criteria.thingId)
      if (focusPlacementEntry(placement)) return embedSnapshot()

      const mapping = Array.from(state.mappingsByNodeKey.values()).find((entry) => entry.thing?.id === criteria.thingId)
      if (focusMappingEntry(mapping)) return embedSnapshot()
    }

    const query = String(criteria.query || "").trim().toLowerCase()
    if (query.length > 0) {
      const entry = state.searchEntries.find((candidate) => candidate.searchText.includes(query))
      if (entry) {
        focusSearchEntry(entry)
        return embedSnapshot()
      }
    }

    throw new Error("Could not find an object matching the requested focus criteria.")
  }

  const embedBridge = createEmbedBridge({
    enabled: state.mode === "embedded",
    getSnapshot: embedSnapshot,
    focusObject: (payload) => focusEmbedObject(payload),
    setViewMode: ({ mode }) => {
      const nextMode = normalizeRequestedViewMode(mode)
      if (!["3d", "2d", "walkthrough"].includes(nextMode)) {
        throw new Error(`Unsupported view mode: ${nextMode}`)
      }
      if (nextMode === "walkthrough" && !state.model) {
        throw new Error("Walkthrough mode is unavailable without a GLB model.")
      }
      queueViewMode(nextMode)
    },
    fitView: () => fitCurrentView(),
    resetView: () => resetView(),
    reload: () => window.location.reload()
  })

  return {
    embedBridge,
    currentSelectionPayload,
    embedSnapshot,
    notifyEmbedStateChanged: () => {
      embedBridge.notifyStateChanged(embedSnapshot())
    },
    notifyEmbedSelectionChanged: () => {
      embedBridge.notifySelectionChanged(currentSelectionPayload(), embedSnapshot())
    },
    focusEmbedObject
  }
}
